import Component from 'vue-class-component';
import {forkJoin, Observable} from 'rxjs';
import {catchError, finalize, map, mergeMap} from 'rxjs/operators';
import PageBase from '@/pages/page-base';
import ForestGameAttributes, {InteractiveObject} from '@/interfaces/entities/forest-game.interface';
import ApiService from '@/services/api.service';
import MapFeaturesService from '@/services/map-features.service';
import ForestGameService from '@/services/forest-game.service';
import GameMapService from '@/services/game-map.service';
import Feature from '@/interfaces/geo/feature.interface';
import {MediaQueryService} from '@/services/media-query.service';
import {ForestGame} from '@/library/forest-game/forest-game';
import {CustomObservable} from '@/library/observable/custom-observable';
import PopupService from '@/services/popup.service';
import AppMapInfoContainer from '@/components/map-info-container/map-info-container.vue';
import TranslationsService from '@/services/translations.service';
import {AxiosResponse} from 'axios';
import GameUserPosition from '@/interfaces/geo/game-user-position';
import {IntervalObservable} from 'rxjs-compat/observable/IntervalObservable';

@Component({})
export default class ForestGamesApp extends PageBase {
    public forestGame: ForestGame = new ForestGame({} as ForestGameAttributes);
    public forestGameService: ForestGameService = ForestGameService.getInstance();
    public mediaQueryService: MediaQueryService = MediaQueryService.getInstance();
    public gameReady: boolean = false;
    private mapFeaturesService: MapFeaturesService = MapFeaturesService.getInstance();
    private gameMapService: GameMapService = GameMapService.getInstance();
    private popupService: PopupService = PopupService.getInstance();
    private apiService = ApiService.getInstance();
    private attemptedInteractiveObject: InteractiveObject | null = null;
    private selectedInteractiveObject: InteractiveObject | null = null;
    private translations: TranslationsService = TranslationsService.getInstance();

    constructor(options: any) {
        super(options);
    }

    public created(): void {
        this.forestGame = new ForestGame(this.apiService.forestGame());
        this.whenPageReady.pipe(
            mergeMap(() => this.gameMapService.renderMap()),
            mergeMap(() => this.mapFeaturesService.loadAllGameLayers()),
            mergeMap(() => this.gameMapService.renderMap()),
            map(() => this.subscribeOnEvents()),
            mergeMap(() => this.updateMapPadding()),
            mergeMap(() => this.gameMapService.selectGame(this.forestGame.lvmGeoId())),
            mergeMap(() => this.mapStartGameIfGameIsInProgress()),
            map(() => {
                if (!this.forestGame.isStarted() || this.forestGame.isEnded()) {
                    this.infoContainerRef().expand();
                }
            }),
            map(() => this.gameReady = true),
        ).subscribe();
    }

    public run(): void {
    }

    public infoContainerRef(): AppMapInfoContainer {
        return this.$refs['infoContainer'] as AppMapInfoContainer;
    }

    public onInfoContentStateChange(isExpanded: boolean): void {
        this.updateMapPadding();
        if (isExpanded && this.isInteractiveObjectOpened()) {
            this.closeInteractiveObject();
        }
    }

    public isTimerVisible(): boolean {
        return this.forestGame.isGameOnTimer() && this.forestGame.isStarted() && !this.forestGame.isEnded();
    }

    public isInteractiveObjectOpened(): boolean {
        return !!this.selectedInteractiveObject;
    }

    public onTimeout(): void {
        this.endGame().subscribe();
    }

    public hideSummary(): void {
        this.forestGameService.hideSummary();
    }

    public showSummary(): void {
        this.forestGameService.showSummary();
    }

    public updateUserCompletedTasks(userCompletedTasks: string[]): void {
        const mapMarkCheckpointsFoundObservables: Observable<void>[] = userCompletedTasks
            .filter((taskId: string) => !this.forestGame.userCompletedTasks().includes(taskId))
            .map((id: string) => this.forestGame.interactiveObject(id))
            .map((object: InteractiveObject | null) => {
                    return (object && object.lvmgeoId)
                        ? this.mapFeaturesService.gameFeatureForGameMapPoint(object.lvmgeoId).pipe(
                            mergeMap((feature: Feature) => this.gameMapService.foundCheckPoint(feature)),
                            catchError(() => CustomObservable.empty()),
                        )
                        : CustomObservable.empty();
                },
            );
        forkJoin(mapMarkCheckpointsFoundObservables).pipe(
            map(() => this.forestGame.updateUserCompletedTasks(userCompletedTasks)),
            mergeMap(() => this.forestGame.isAllObjectsCompleted() ? this.endGame() : CustomObservable.empty()),
        ).subscribe();
    }

    public updateGameStartTime(startTime: string): void {
        this.forestGame.updateGameStartTime(startTime);
    }

    public updateGameEndTime(endTime: string): void {
        this.forestGame.updateGameEndTime(endTime);
        if (!this.mediaQueryService.aboveMd) {
            this.infoContainerRef().collapse();
        }
    }

    public openInteractiveObjectFromList(interactiveObject: InteractiveObject): void {
        if (interactiveObject.lvmgeoId) {
            this.gameMapService.selectCheckpoint(interactiveObject.lvmgeoId).subscribe();
        }
        this.openInteractiveObject(interactiveObject);
    }

    public closeInteractiveObject(): void {
        this.selectedInteractiveObject = null;
        this.gameMapService.unselectCheckpoint().subscribe();
    }

    public messageTitle(): string {
        const type = (this.forestGame.isTimeout()) ? 'timeout' : (this.forestGame.correctAnswers() === 0) ? 'totalNull' : 'default';
        const sources: { [key: string]: string; } = {
            'timeout': this.translations.translation('COM_MAMMADABA_MAP_FOREST_GAME_TIMEOUT'),
            'totalNull': '',
            'default': this.translations.translation('COM_MAMMADABA_MAP_FOREST_GAME_CONGRATULATION'),
        };
        return sources[type];
    }

    public resultText(): string {
        const message = this.translations.translation('COM_MAMMADABA_MAP_FOREST_GAME_COMPLETED_SUMMARY');
        const result = [this.forestGame.correctAnswers().toString(), this.forestGame.interactiveObjects().length.toString()];
        return message.replace(/\$(\d+)/g, (match, index) => result[index])
    }

    private mapStartGameIfGameIsInProgress(): Observable<void> {
        return this.forestGame.isStarted() && !this.forestGame.isEnded()
            ? this.gameMapService.startGame(this.forestGame.lvmGeoId())
            : CustomObservable.empty();
    }

    private openInteractiveObjectFromMapFeature(feature: Feature): void {
        if (this.forestGame.isStarted() && !this.forestGame.isEnded() && !this.forestGame.isMapCheckPointCompleted(feature)) {
            const interactiveObject: InteractiveObject | null = this.forestGame.interactiveObjectForFeature(feature);
            if (interactiveObject) {
                this.openInteractiveObject(interactiveObject);
            }
        }
    }

    private openInteractiveObject(interactiveObject: InteractiveObject): void {
        if (this.gameMapService.isFeatureApproached(interactiveObject.lvmgeoId || '')) {
            this.selectedInteractiveObject = interactiveObject;
            this.hideSummary();
            if (!this.mediaQueryService.aboveMd) {
                this.infoContainerRef().collapse();
            }
        } else {
            this.attemptedInteractiveObject = interactiveObject;
            this.popupService.open('infoNeedToBeNearbyCheckpoint').subscribe(() => {
                this.attemptedInteractiveObject = null;
            });
        }
    }

    private endGame(): Observable<void> {
        return this.setResult().pipe(
            map(() => {
                    this.showSummary();
                },
            ),
            mergeMap(() => this.gameMapService.completeGame(this.forestGame.lvmGeoId())),
        )
    }

    private subscribeOnEvents(): void {
        this.gameMapService.whenGameSelected.subscribe((feature: Feature) => {
        });
        this.gameMapService.whenGameCompleted.subscribe((feature: Feature) => {
        });
        this.gameMapService.whenCheckPointSelected.subscribe((feature: Feature) => {
            this.openInteractiveObjectFromMapFeature(feature);
        });
        this.gameMapService.whenCheckPointUnselected.subscribe(() => {
            this.closeInteractiveObject();
        });
        this.gameMapService.whenGameLocationChanged.subscribe((gameUserPosition: GameUserPosition) => {
            if (this.attemptedInteractiveObject) {
                const attemptingInteractiveObject: InteractiveObject = this.attemptedInteractiveObject;
                const approached: boolean = gameUserPosition.approachingFeatures.some((feature: Feature): boolean => {
                    return feature.values_.POINTID.toString() === attemptingInteractiveObject.lvmgeoId;
                });
                if (approached) {
                    this.openInteractiveObject(this.attemptedInteractiveObject);
                    this.popupService.close('infoNeedToBeNearbyCheckpoint');
                }
            }
        })
    }

    private updateMapPadding(): Observable<void> {
        return this.gameMapService.changePadding(this.infoContainerRef().width());
    }

    private setResult(): Observable<void> {
        this.forestGame.loader = !this.popupService.isOpened('infoDoubleAnswers');
        return this.forestGameService.result(this.forestGame).pipe(
            map((response: AxiosResponse) => {
                this.forestGame.updateCorrectAnswers(response.data.data.correctAnswersCount)
                if(!this.popupService.isOpened('infoDoubleAnswers')){
                this.popupService.open('infoGameCompleted').subscribe();
                }
            }),
            finalize(() => this.forestGame.loader = false),
        )
    }
}
